"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import type { NftItem, NftsResponse } from "@/features/nfts/types";
import { useCollectionTraits } from "@/features/collections/hooks/useCollectionListings";
import { useDebounce } from "@/hooks/useDebounce";
import { useInfiniteGrid } from "@/hooks/useInfiniteGrid";
import { useInfiniteScrollSentinel } from "@/hooks/useInfiniteScrollSentinel";
import { useBrowseParams } from "@/lib/browse/params";
import { countItemFilters, isItemFilterKey, ITEM_SORT_PRESETS, itemFilterParams, readItemFilters, readItemSort, toggleTrait, writeItemFilters, type ItemFilters, type ItemSort, type TraitPick } from "./itemFilters";

/** The items panel owns `?sort=` itself (`ITEM_SORT_PRESETS`), so the shared schema is given none. */
const NO_SHARED_SORTS = [] as const;

/**
 * THE ITEMS TAB'S DATA — the legacy `CollectionItemsPanel` + `NftsGrid`
 * load-more path, with the filters `/nfts` now takes:
 * - URL state on the shared schema: `?q=` → `name`, `?sort=`, `?page=` as the
 *   entry point; the filters under the API's own names (`itemFilters.ts`);
 * - the search is debounced between the URL and the request (500ms);
 * - the trait catalogue is the collection's own (`/collections/{c}/traits`);
 * - `/nfts` in load-more mode with the three SSR seed tests — never an
 *   `offline_fallback`, never under a filter, never under a later entry
 *   page — and an out-of-range `?page=` clamped to the server's last page;
 * - accumulation bounded by `useInfiniteGrid`'s page cap, continued by a
 *   `?page=` re-entry that releases the retained window.
 */
export function useCollectionItems(contract, lang, initialData, pageSize) {
  const $ = _c(57);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      sortKeys: NO_SHARED_SORTS
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const params = useBrowseParams(t0);
  const {
    q,
    page,
    networkId,
    set,
    clearAll
  } = params;
  const searchParams = useSearchParams();
  let t1;
  if ($[1] !== searchParams) {
    t1 = searchParams.toString();
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const search = t1;
  let t2;
  if ($[3] !== searchParams) {
    t2 = readItemSort(searchParams.get("sort"));
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sort = t2;
  let t3;
  if ($[5] !== search) {
    t3 = readItemFilters(new URLSearchParams(search));
    $[5] = search;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const filters = t3;
  const debouncedQuery = useDebounce(q, 500);
  const traitsQuery = useCollectionTraits(contract, networkId);
  let t4;
  if ($[7] !== traitsQuery.data?.data?.traits) {
    t4 = traitsQuery.data?.data?.traits ?? [];
    $[7] = traitsQuery.data?.data?.traits;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = t4.filter(_temp);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const catalogue = t5;
  const {
    orderBy,
    order
  } = ITEM_SORT_PRESETS[sort];
  let t6;
  if ($[11] !== filters) {
    t6 = itemFilterParams(filters);
    $[11] = filters;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const sent = t6;
  const filterCount = countItemFilters(filters);
  const seedMeta = initialData?.message === "offline_fallback" ? undefined : initialData?.data?.page;
  const lastKnown = seedMeta?.last_page;
  const entryPage = typeof lastKnown === "number" && lastKnown > 0 ? Math.min(page, lastKnown) : page;
  const seedMatchesFilters = !debouncedQuery && filterCount === 0 && sort === "newest";
  const seed = initialData?.message !== "offline_fallback" && seedMatchesFilters && entryPage === 1 ? initialData : undefined;
  let t7;
  if ($[13] !== contract || $[14] !== debouncedQuery || $[15] !== order || $[16] !== orderBy || $[17] !== sent) {
    t7 = {
      name: debouncedQuery,
      contract_address: contract,
      order_by: orderBy,
      order,
      ...sent
    };
    $[13] = contract;
    $[14] = debouncedQuery;
    $[15] = order;
    $[16] = orderBy;
    $[17] = sent;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== entryPage || $[20] !== lang || $[21] !== pageSize || $[22] !== seed || $[23] !== t7) {
    t8 = {
      initialData: seed,
      apiEndpoint: "/nfts",
      lang,
      pageSize,
      initialPage: entryPage,
      queryParams: t7
    };
    $[19] = entryPage;
    $[20] = lang;
    $[21] = pageSize;
    $[22] = seed;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const grid = useInfiniteGrid(t8);
  let t9;
  if ($[25] !== grid.hasMore || $[26] !== grid.isFetchingMore || $[27] !== grid.loadMore) {
    t9 = {
      hasMore: grid.hasMore,
      isFetching: grid.isFetchingMore,
      onLoadMore: grid.loadMore
    };
    $[25] = grid.hasMore;
    $[26] = grid.isFetchingMore;
    $[27] = grid.loadMore;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const sentinelRef = useInfiniteScrollSentinel(t9);
  const hasActiveFilters = Boolean(q) || sort !== "newest" || filterCount > 0;
  const pushQuery = _temp2;
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = patch => pushQuery(sp => writeItemFilters(sp, patch));
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const setFilters = t10;
  const t11 = grid.total ?? (seedMatchesFilters ? seedMeta?.total : undefined) ?? null;
  let t12;
  if ($[30] !== set) {
    t12 = next_0 => set({
      q: next_0 || null
    }, {
      history: "replace"
    });
    $[30] = set;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = next_1 => pushQuery(sp_0 => {
      if (next_1 === "newest") {
        sp_0.delete("sort");
      } else {
        sp_0.set("sort", next_1);
      }
      sp_0.delete("page");
      return sp_0;
    });
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== filters) {
    t14 = pick => setFilters({
      traits: toggleTrait(filters.traits, pick)
    });
    $[33] = filters;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => pushQuery(_temp3);
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== clearAll || $[37] !== networkId) {
    t16 = () => clearAll(networkId ? {
      network_id: networkId
    } : {});
    $[36] = clearAll;
    $[37] = networkId;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== set) {
    t17 = target => {
      set({
        page: target
      }, {
        resetPage: false
      });
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth"
      });
    };
    $[39] = set;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== catalogue || $[42] !== filterCount || $[43] !== filters || $[44] !== grid || $[45] !== hasActiveFilters || $[46] !== q || $[47] !== sentinelRef || $[48] !== sort || $[49] !== t11 || $[50] !== t12 || $[51] !== t14 || $[52] !== t16 || $[53] !== t17 || $[54] !== traitsQuery.isError || $[55] !== traitsQuery.isLoading) {
    t18 = {
      q,
      sort,
      filters,
      filterCount,
      catalogue,
      traitsLoading: traitsQuery.isLoading,
      traitsFailed: traitsQuery.isError,
      grid,
      sentinelRef,
      total: t11,
      hasActiveFilters,
      setQuery: t12,
      setSort: t13,
      setFilters,
      toggleTrait: t14,
      clearFilters: t15,
      clearAll: t16,
      continueAt: t17
    };
    $[41] = catalogue;
    $[42] = filterCount;
    $[43] = filters;
    $[44] = grid;
    $[45] = hasActiveFilters;
    $[46] = q;
    $[47] = sentinelRef;
    $[48] = sort;
    $[49] = t11;
    $[50] = t12;
    $[51] = t14;
    $[52] = t16;
    $[53] = t17;
    $[54] = traitsQuery.isError;
    $[55] = traitsQuery.isLoading;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  return t18;
}
function _temp3(sp_1) {
  Array.from(sp_1.keys()).filter(isItemFilterKey).forEach(k => sp_1.delete(k));
  sp_1.delete("page");
  return sp_1;
}
function _temp2(edit) {
  if (typeof window === "undefined") {
    return;
  }
  const next = edit(new URLSearchParams(window.location.search)).toString();
  const url = next ? `${window.location.pathname}?${next}` : window.location.pathname;
  if (url !== `${window.location.pathname}${window.location.search}`) {
    window.history.pushState(null, "", url);
  }
}
function _temp(g) {
  return g?.trait && Array.isArray(g.values) && g.values.length > 0;
}
export type CollectionItems = ReturnType<typeof useCollectionItems>;